/**
 * 对齐与分布：位移计算是**纯函数**（可在 node 里断言），
 * 落盘时一条命令装 N 个 patch（一次撤销全部回退）。
 */
import type { Pt, Rect } from '../geom/matrix';
import type { Command } from '../history';
import { moveEachBy, type TransformDeps } from './transform';

export type AlignMode = 'left' | 'hcenter' | 'right' | 'top' | 'vcenter' | 'bottom';
export type DistributeMode = 'horizontal' | 'vertical';

export const ALIGN_LABEL: Record<AlignMode, string> = {
  left: '左对齐',
  hcenter: '水平居中',
  right: '右对齐',
  top: '顶对齐',
  vcenter: '垂直居中',
  bottom: '底对齐',
};

export function unionRect(boxes: readonly Rect[]): Rect | null {
  if (boxes.length === 0) return null;
  const x = Math.min(...boxes.map((b) => b.x));
  const y = Math.min(...boxes.map((b) => b.y));
  const x2 = Math.max(...boxes.map((b) => b.x + b.w));
  const y2 = Math.max(...boxes.map((b) => b.y + b.h));
  return { x, y, w: x2 - x, h: y2 - y };
}

const ZERO: Pt = { x: 0, y: 0 };

export function alignOffsets(boxes: readonly Rect[], mode: AlignMode): Pt[] {
  const u = unionRect(boxes);
  if (!u) return boxes.map(() => ZERO);
  return boxes.map((b) => {
    switch (mode) {
      case 'left':
        return { x: u.x - b.x, y: 0 };
      case 'right':
        return { x: u.x + u.w - (b.x + b.w), y: 0 };
      case 'hcenter':
        return { x: u.x + u.w / 2 - (b.x + b.w / 2), y: 0 };
      case 'top':
        return { x: 0, y: u.y - b.y };
      case 'bottom':
        return { x: 0, y: u.y + u.h - (b.y + b.h) };
      case 'vcenter':
        return { x: 0, y: u.y + u.h / 2 - (b.y + b.h / 2) };
    }
  });
}

/** 分布：两端元素不动，中间等间距（需 ≥3 个元素） */
export function distributeOffsets(boxes: readonly Rect[], mode: DistributeMode): Pt[] {
  const n = boxes.length;
  if (n < 3) return boxes.map(() => ZERO);
  const horiz = mode === 'horizontal';
  const key = (b: Rect): number => (horiz ? b.x + b.w / 2 : b.y + b.h / 2);
  const size = (b: Rect): number => (horiz ? b.w : b.h);

  const order = boxes.map((_, i) => i).sort((a, c) => key(boxes[a] as Rect) - key(boxes[c] as Rect));
  const first = boxes[order[0] as number] as Rect;
  const last = boxes[order[n - 1] as number] as Rect;
  const start = horiz ? first.x : first.y;
  const end = horiz ? last.x + last.w : last.y + last.h;
  const occupied = order.reduce((sum, i) => sum + size(boxes[i] as Rect), 0);
  const gap = (end - start - occupied) / (n - 1);

  const offsets: Pt[] = boxes.map(() => ZERO);
  let cursor = start;
  for (const i of order) {
    const b = boxes[i] as Rect;
    offsets[i] = horiz ? { x: cursor - b.x, y: 0 } : { x: 0, y: cursor - b.y };
    cursor += size(b) + gap;
  }
  return offsets;
}

export const alignCommand = (
  deps: TransformDeps,
  els: Element[],
  boxes: readonly Rect[],
  mode: AlignMode,
): Command => moveEachBy(deps, els, alignOffsets(boxes, mode), ALIGN_LABEL[mode]);

export const distributeCommand = (
  deps: TransformDeps,
  els: Element[],
  boxes: readonly Rect[],
  mode: DistributeMode,
): Command =>
  moveEachBy(deps, els, distributeOffsets(boxes, mode), mode === 'horizontal' ? '水平分布' : '垂直分布');
